if (!customElements.get('variant-media-latori')) {
  /**
   * Restricts the product gallery to the selected variant's own image followed
   * by the product's featured image (plus any `all-groups` media), for every
   * product with variant options whose variants carry their own media.
   * Replaces Symmetry's media grouping, which only handles the single option
   * name configured in the section's `media_grouping_option` setting, never
   * runs on a reloaded `?variant=` URL, and can't order the two images.
   *
   * Rendered only when `variant_media_only` is true in
   * sections/main-product.liquid, which also renders the initial state -
   * `hidden` attributes (via snippets/variant-media-hidden-latori.liquid) and
   * the variant-image-first order - so the first paint is already correct;
   * this component takes over from the next variant change onwards.
   *
   * Mirrors MediaGallery.setActiveMediaGroup() in assets/main.js: media is
   * toggled with the `hidden` attribute and never removed from the DOM, both
   * because MediaGallery.setActiveMedia() looks media up by ID among the
   * already-rendered elements and because carousel-slider's refresh() picks its
   * slides with `.slider__item:not([hidden])`.
   */
  class VariantMediaLatori extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      if (!this.gallery) return;

      this.section = this.closest('.js-product');
      this.alwaysVisibleIds = (this.dataset.alwaysVisibleIds || '').split(',').filter(Boolean);
      // The selected variant's own media, which stays visible no matter which
      // of the visible images the customer is currently looking at. Rendered
      // server-side for the initially selected variant, updated on every
      // variant change below.
      this.variantMediaId = this.dataset.variantMediaId || null;
      this.mediaLists = [
        this.gallery.querySelectorAll('.main-image .slider__item'),
        this.gallery.querySelectorAll('.product-media-collage__item'),
        this.gallery.querySelectorAll('.thumbnails .slider__item')
      ];
      this.zoomThumbsAdded = false;

      // `on:media-gallery:change` is dispatched at the end of
      // MediaGallery.setActiveMedia() - both on a variant change and when the
      // customer picks one of the visible thumbnails.
      this.handleMediaChange = this.update.bind(this);
      this.gallery.addEventListener('on:media-gallery:change', this.handleMediaChange);

      // MediaGallery's own `on:variant:change` listener is registered first
      // (its element is this one's ancestor), so by the time this runs the
      // gallery has already moved its active media on.
      this.handleVariantChange = (evt) => {
        const variant = evt.detail && evt.detail.variant;
        if (variant && variant.featured_media) {
          this.variantMediaId = variant.featured_media.id.toString();
        }
        this.update();
      };
      if (this.section) {
        this.section.addEventListener('on:variant:change', this.handleVariantChange);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.gallery) {
        this.gallery.removeEventListener('on:media-gallery:change', this.handleMediaChange);
      }
      if (this.section) {
        this.section.removeEventListener('on:variant:change', this.handleVariantChange);
      }
    }

    /**
     * The zoom viewer's thumbnails live in a detached clone of the
     * `.js-media-zoom-template` markup that MediaGallery keeps in
     * `galleryModal` (added to the document on first open), so they can only be
     * picked up once MediaGallery's own connectedCallback has run. Their
     * initial state is rendered server-side in snippets/gallery-viewer.liquid;
     * from here on they are hidden and reordered like every other media list -
     * GalleryViewer.selectPreviousThumb()/selectNextThumb() already skip
     * thumbnails without an `offsetParent`, which is what Symmetry's own media
     * grouping relied on.
     */
    addZoomViewerThumbs() {
      if (this.zoomThumbsAdded || !this.gallery.galleryModal) return;
      const zoomThumbs = this.gallery.galleryModal.querySelectorAll('.gallery-viewer__thumb');
      if (zoomThumbs.length) this.mediaLists.push(zoomThumbs);
      this.zoomThumbsAdded = true;
    }

    /**
     * @returns {?string} Media ID of the gallery's currently active media.
     */
    getActiveMediaId() {
      const activeItem = this.gallery.querySelector(
        '.main-image .slider__item.is-active, .thumbnails .slider__item.is-active, .product-media-collage__item.is-active'
      );
      return activeItem ? activeItem.dataset.mediaId : null;
    }

    /**
     * Hides every media item apart from the selected variant's own image, the
     * always-visible ones and whichever image is currently active, then
     * re-initialises the sliders so their nav buttons, dynamic height and
     * scroll position match the reduced set of slides.
     *
     * The active image has to stay in that set on its own account: switching
     * to the featured image via its thumbnail makes it the active one while
     * the variant's image stays visible alongside it, and on a variant change
     * the gallery moves the active media on before `variantMediaId` is
     * updated below.
     */
    update() {
      const activeId = this.getActiveMediaId();
      if (!activeId) return;

      this.addZoomViewerThumbs();

      const visibleIds = [activeId, ...this.alwaysVisibleIds];
      if (this.variantMediaId) visibleIds.push(this.variantMediaId);

      this.mediaLists.forEach((list) => {
        if (list.length === 0) return;

        list.forEach((item) => {
          item.hidden = !visibleIds.includes(item.dataset.mediaId);
        });

        // Selected variant's image first, featured image second - the same
        // order main-product.liquid renders, re-applied after a variant
        // change. MediaGallery.setActiveMediaGroup() moves its `all-groups`
        // media around the same way.
        const variantItem = [...list].find((item) => item.dataset.mediaId === this.variantMediaId);
        if (variantItem && variantItem.previousElementSibling) {
          variantItem.parentElement.prepend(variantItem);
        }

        // Same guard as MediaGallery.setActiveMediaGroup(): only sliders that
        // are actually on screen. refresh() copes with a slider that never
        // initialised - a gallery showing a single image has fewer than the
        // two slides CarouselSlider needs, so it stays un-initialised until
        // unhiding a second one here.
        const slider = list[0].closest('carousel-slider');
        if (!slider || !slider.offsetParent || typeof slider.refresh !== 'function') return;

        slider.refresh();

        // The active media may have been hidden while setActiveMedia() ran, in
        // which case its own scrollToElement() call was a no-op (the slide
        // wasn't among the slider's slides yet).
        const activeItem = [...list].find((item) => item.dataset.mediaId === activeId);
        if (activeItem) slider.scrollToElement(activeItem, 'instant');
      });
    }
  }

  customElements.define('variant-media-latori', VariantMediaLatori);
}
